import React, { useState, useEffect } from 'react';
import { Snackbar, Alert, IconButton, Slide } from '@mui/material';
import { createRoot } from 'react-dom/client';
import { AiOutlineClose, AiOutlineCheckCircle, AiOutlineCloseCircle, AiOutlineInfoCircle, AiOutlineWarning } from 'react-icons/ai';

let snackbarContainer = null;
let snackbarRoot = null;
let globalSnackbars = [];

const createSnackbarContainer = () => {
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.top = '20px';
  container.style.right = '20px';
  container.style.zIndex = '9999';
  document.body.appendChild(container);
  return container;
};

const getToastIcon = (type) => {
  switch (type) {
    case 'success':
      return <AiOutlineCheckCircle style={{ color: '#28a745', fontSize: 18, marginRight: 10 }} />;
    case 'error':
      return <AiOutlineCloseCircle style={{ color: '#dc3545', fontSize: 18, marginRight: 10 }} />;
    case 'warning':
      return <AiOutlineWarning style={{ color: '#ffc107', fontSize: 18, marginRight: 10 }} />;
    case 'info':
    default:
      return <AiOutlineInfoCircle style={{ color: '#17a2b8', fontSize: 18, marginRight: 10 }} />;
  }
};

const SnackbarManager = ({ snackbars, onRemove }) => (
  <>
    {snackbars.map((snack) => (
      <Snackbar
        key={snack.id}
        open={true}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={snack.autoClose}
        onClose={() => onRemove(snack.id)}
        TransitionComponent={Slide}
        sx={{
          mb: 2,
          '& .MuiPaper-root': {
            background: '#fff',
            color: '#222',
            minWidth: 300,
            maxWidth: 400,
            boxShadow: 3,
            paddingRight: '15px',
          },
        }}
      >
        <Alert
          severity={snack.type}
          icon={false}
          sx={{
            width: '100%',
            background: '#fff',
            color:
              snack.type === 'success'
                ? '#4caf50'
                : snack.type === 'error'
                  ? '#f44336'
                  : snack.type === 'warning'
                    ? '#ff9800'
                    : '#2196f3',
            fontWeight: 500,
            alignItems: 'center',
            pr: snack.closeOnClick ? 4 : 2,
          }}
          action={
            snack.closeOnClick && (
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={() => onRemove(snack.id)}
                sx={{ padding: '0' }}
              // sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <AiOutlineClose fontSize="small" />
              </IconButton>
            )
          }
        >
          <div style={{ display: 'flex', alignItems: 'center', position: 'relative', paddingRight: 32 }}>
            {getToastIcon(snack.type)}
            <div style={{ flex: 1, fontSize: 12 }}>{snack.message}</div>
          </div>
          {snack.autoClose > 0 && (
            <div
              style={{
                height: 4,
                width: '100%',
                borderRadius: 2,
                overflow: 'hidden',
                position: 'absolute',
                left: 0,
                bottom: 0,
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: '100%',
                  background:
                    snack.type === 'success'
                      ? '#28a745'
                      : snack.type === 'error'
                        ? '#dc3545'
                        : snack.type === 'warning'
                          ? '#ffc107'
                          : '#17a2b8',
                  transform: 'translateX(-100%)',
                  animation: `progressBarAnim ${snack.autoClose}ms linear forwards`,
                }}
              />
              <style>
                {`
                  @keyframes progressBarAnim {
                    from { transform: translateX(-100%); }
                    to { transform: translateX(0); }
                  }
                `}
              </style>
            </div>
          )}
        </Alert>
      </Snackbar>
    ))}
  </>
);

const SnackbarApp = () => {
  const [snackbars, setSnackbars] = useState(globalSnackbars);

  useEffect(() => {
    setSnackbars(globalSnackbars);
  }, [globalSnackbars]);

  const removeSnackbar = (id) => {
    globalSnackbars = globalSnackbars.filter((snack) => snack.id !== id);
    setSnackbars(globalSnackbars);
  };

  return <SnackbarManager snackbars={snackbars} onRemove={removeSnackbar} />;
};

export const showSnackbar = ({
  type = 'success',
  message = '',
  autoClose = 3000,
  closeOnClick = true,
}) => {
  if (!snackbarContainer) {
    snackbarContainer = createSnackbarContainer();
    snackbarRoot = createRoot(snackbarContainer);
    snackbarRoot.render(<SnackbarApp />);
  }

  const snackId = Date.now() + Math.random();
  const newSnack = {
    id: snackId,
    type,
    message,
    autoClose,
    closeOnClick,
  };

  globalSnackbars = [...globalSnackbars, newSnack];
  snackbarRoot.render(<SnackbarApp />);

  if (autoClose > 0) {
    setTimeout(() => {
      globalSnackbars = globalSnackbars.filter((snack) => snack.id !== snackId);
      snackbarRoot.render(<SnackbarApp />);
    }, autoClose);
  }
};